import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useCompetitionLanding } from '@/hooks/useCompetitions';
import { useRegister } from '@/hooks/useAuth';
import { getApiErrorMessage } from '@/api/client';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';

interface FormState {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

type FormErrors = Partial<Record<keyof FormState, string>>;

const initialState: FormState = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

export function RegisterPage() {
  const [form, setForm] = useState<FormState>(initialState);
  const [errors, setErrors] = useState<FormErrors>({});
  const register = useRegister();
  const navigate = useNavigate();
  // There is no open registration: an account is created from a competition's own link and joins that competition.
  const competitionId = useSearchParams()[0].get('competition') ?? undefined;
  const landing = useCompetitionLanding(competitionId);
  const competitionPath = competitionId ? `/competitions/${competitionId}` : '/';
  const loginState = { from: { pathname: competitionPath } };

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function validate(): boolean {
    const next: FormErrors = {};
    if (!form.firstName.trim()) next.firstName = 'Ime je obvezno.';
    if (!form.lastName.trim()) next.lastName = 'Priimek je obvezen.';
    if (!form.email.trim()) next.email = 'E-pošta je obvezna.';
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Vnesite veljaven e-poštni naslov.';
    if (!form.password) next.password = 'Geslo je obvezno.';
    else if (form.password.length < 8) next.password = 'Geslo mora vsebovati vsaj 8 znakov.';
    if (form.confirmPassword !== form.password) next.confirmPassword = 'Gesli se ne ujemata.';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate() || !competitionId) return;
    try {
      await register.mutateAsync({
        email: form.email,
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        competitionId,
      });
      // Straight into the competition, where the upload form is.
      navigate(competitionPath, { replace: true });
    } catch {
      // surfaced via register.error below
    }
  }

  if (!competitionId || landing.isError || (landing.data && !landing.data.isOpenForSignup)) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-lg font-semibold text-fg">Registracija ni mogoča</h1>
        <p className="text-sm text-fg-secondary">
          Račun lahko ustvarite samo prek povezave na tekmovanje, ki sprejema prijave. Povezavo vam posreduje organizator
          tekmovanja.
        </p>
        <Link to="/login" className="text-sm font-medium text-accent-text hover:underline">
          Že imate račun? Prijavite se
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
      <h1 className="text-lg font-semibold text-fg">Ustvarite račun</h1>
      {landing.data && (
        <p className="text-sm text-fg-secondary">
          Prijavili se boste na tekmovanje <span className="font-medium text-fg">{landing.data.name}</span>.
        </p>
      )}

      {register.isError && <Alert variant="error">{getApiErrorMessage(register.error)}</Alert>}

      <div className="grid grid-cols-2 gap-3">
        <Input
          label="Ime"
          name="firstName"
          value={form.firstName}
          onChange={(e) => update('firstName', e.target.value)}
          error={errors.firstName}
        />
        <Input
          label="Priimek"
          name="lastName"
          value={form.lastName}
          onChange={(e) => update('lastName', e.target.value)}
          error={errors.lastName}
        />
      </div>
      <Input
        label="E-pošta"
        type="email"
        name="email"
        autoComplete="email"
        value={form.email}
        onChange={(e) => update('email', e.target.value)}
        error={errors.email}
      />
      <Input
        label="Geslo"
        type="password"
        name="password"
        autoComplete="new-password"
        value={form.password}
        onChange={(e) => update('password', e.target.value)}
        error={errors.password}
      />
      <Input
        label="Potrdite geslo"
        type="password"
        name="confirmPassword"
        autoComplete="new-password"
        value={form.confirmPassword}
        onChange={(e) => update('confirmPassword', e.target.value)}
        error={errors.confirmPassword}
      />

      <Button type="submit" isLoading={register.isPending} className="mt-2 w-full">
        Ustvari račun
      </Button>

      <p className="text-center text-sm text-fg-muted">
        Že imate račun?{' '}
        <Link to="/login" state={loginState} className="font-medium text-accent-text hover:underline">
          Prijavite se
        </Link>
      </p>
    </form>
  );
}
